'use client';

/**
 * McpClaudeSetup — the 30-second install.
 *
 * Single-command setup is the differentiator vs. "build your own MCP".
 * One clean copy-paste block + 3-step explanation. Zero pricing,
 * zero plan tables — focus on capability per the user's instruction.
 */

import React from 'react';
import { Locale } from '../../../../i18n-config';
import SectionLabel from '../home/SectionLabel';
import StepCards, { StepCardItem } from '../_shared/sections/StepCards';
import { useTranslation } from '@/i18n/client';
import { HiCommandLine, HiCubeTransparent, HiSparkles } from 'react-icons/hi2';

type Props = { lang: Locale };

const SETUP_COMMAND = 'claude mcp add sum-api --transport sse https://mcp.super-user-manager.com/sse';

type StepKey = 'install' | 'connect' | 'build';

const STEP_NUMS: Record<StepKey, string> = {
  install: '01',
  connect: '02',
  build: '03',
};

const STEP_ICONS: Record<StepKey, StepCardItem['Icon']> = {
  install: HiCubeTransparent,
  connect: HiCommandLine,
  build: HiSparkles,
};

const KEYS: StepKey[] = ['install', 'connect', 'build'];

const McpClaudeSetup = ({ lang }: Props) => {
  const { t } = useTranslation(lang);

  const label = t('McpClaude/Setup/badge');
  const headlineA = t('McpClaude/Setup/title1');
  const headlineB = t('McpClaude/Setup/title2');
  const sub = t('McpClaude/Setup/subtitle');

  const steps: StepCardItem[] = KEYS.map((k) => ({
    key: k,
    num: STEP_NUMS[k],
    title: t(`McpClaude/Setup/${k}/title`),
    description: t(`McpClaude/Setup/${k}/body`),
    Icon: STEP_ICONS[k],
  }));

  return (
    <section
      id="how-it-works"
      className="px-6 py-24 lg:py-28"
      style={{
        background: '#0E1130',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        color: '#F5F7FA',
      }}
    >
      <div className="mx-auto max-w-[1200px]">
        <SectionLabel num="02" label={label} />
        <h2
          className="font-bold tracking-tight"
          style={{
            fontSize: 'clamp(28px, 3.4vw, 48px)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            margin: '12px 0 14px',
            maxWidth: 820,
          }}
        >
          {headlineA} <span style={{ color: '#B4C1D8' }}>{headlineB}</span>
        </h2>
        <p
          style={{
            color: '#B4C1D8',
            fontSize: 18,
            maxWidth: 720,
            lineHeight: 1.5,
            margin: '0 0 32px',
          }}
        >
          {sub}
        </p>

        <div
          className="rounded-2xl mb-12 overflow-x-auto"
          style={{
            background: 'rgba(34,211,238,0.06)',
            border: '1px solid rgba(34,211,238,0.30)',
            padding: '20px 24px',
          }}
        >
          <div
            className="mono"
            style={{
              fontSize: 11,
              letterSpacing: 1.5,
              color: '#22D3EE',
              textTransform: 'uppercase',
              marginBottom: 8,
            }}
          >
            {t('McpClaude/Setup/command_label')}
          </div>
          <code
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 14,
              color: '#F5F7FA',
              whiteSpace: 'pre',
              display: 'block',
            }}
          >
            $ {SETUP_COMMAND}
          </code>
        </div>

        <StepCards steps={steps} accent="#22D3EE" minCardWidth={260} showArrow />
      </div>
    </section>
  );
};

export default McpClaudeSetup;
